import { useCallback, useEffect, useRef, useState } from "react";
import { requestGuestLink } from "../../lib/api.ts";
import { guestLinkErrorText } from "./call-row.ts";

/**
 * Ссылка для преподавателей на звонок (5.1 и 9.2 `docs/teacher-calls-design.md`):
 * запрос и копирование. Общая у строки списка «Уроки» и у блока ссылки в
 * комнате звонка; комната берёт её отсюда, обратное направление запрещено
 * границей чанков (6.4 проекта).
 *
 * ⚠️ **Ссылка несёт ключ, и правило у неё то же, что у ссылки ученика на
 * занятие** (шапка `room/StudentLessonLink.tsx`): адрес живёт только в
 * состоянии того, кто его показывает, и ни в заметку о неточности, ни в
 * телеметрию не кладётся. Здесь он не лежит нигде, кроме состояния хука, и
 * только пока его нечем забрать, кроме поля.
 *
 * ⚠️ **Ссылка спрашивается по нажатию, а не при отрисовке строки.** Первый
 * запрос заводит ключ в базе, а ведущий, отозвавший ссылку в комнате, получил
 * бы новую молча, просто открыв список уроков: отзыв перестал бы что-либо
 * значить. Цена названа: запись в буфер обмена после ответа сети Safari
 * отклоняет (жест «истёк»), и там вместо «Скопировано» появляется поле с
 * адресом, откуда его забирают кнопкой рядом.
 */

/** Записать текст в буфер обмена. `false`: буфер недоступен или запрещён. */
export async function copyText(text: string): Promise<boolean> {
  try {
    // Отсутствие `navigator.clipboard` (незащищённый контекст) тоже сюда:
    // синхронный отказ внутри `async` это отклонённое обещание
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/** Где сейчас копирование ссылки. */
export type GuestLinkCopyState =
  | { phase: "idle" }
  | { phase: "busy" }
  | { phase: "done" }
  /** Буфер обмена отказал: адрес показан полем, пока его не закроют. */
  | { phase: "manual"; url: string }
  | { phase: "failed"; text: string };

/**
 * Скопировать ссылку одним нажатием: запрос, затем буфер обмена, при отказе
 * буфера поле с адресом. «Скопировано» гаснет само, поле и отказ нет: поле это
 * последний путь к адресу, и гасить его по таймеру значило бы отнять и его.
 */
export function useGuestLinkCopy(lessonId: string): {
  state: GuestLinkCopyState;
  copy: () => void;
  close: () => void;
} {
  const [state, setState] = useState<GuestLinkCopyState>({ phase: "idle" });
  const alive = useRef(true);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // Ставится и здесь, а не только начальным значением: StrictMode в деве
    // снимает эффект и ставит заново, и после снятия флаг остался бы ложным
    alive.current = true;
    return () => {
      alive.current = false;
      if (timer.current !== null) clearTimeout(timer.current);
    };
  }, []);

  const copy = useCallback(() => {
    // Прежнее «Скопировано» не должно погасить новый запрос посреди пути
    if (timer.current !== null) clearTimeout(timer.current);
    setState({ phase: "busy" });
    requestGuestLink(lessonId).then(
      async ({ url }) => {
        const copied = await copyText(url);
        if (!alive.current) return;
        if (!copied) {
          setState({ phase: "manual", url });
          return;
        }
        setState({ phase: "done" });
        timer.current = setTimeout(() => setState({ phase: "idle" }), 2500);
      },
      (e: unknown) => {
        if (alive.current) setState({ phase: "failed", text: guestLinkErrorText(e) });
      },
    );
  }, [lessonId]);

  const close = useCallback(() => setState({ phase: "idle" }), []);

  return { state, copy, close };
}
